//region :: Imports ::
import React, {createRef, ReactElement, RefObject} from 'react';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import MForm from '../../Components/Toolkit/Form';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MComponent from '../../Toolkit/Components/Component';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import MFormSection from '../../Toolkit/Components/Controls/FormSection';
import {MFormOutcome} from '../../Toolkit/Components/Controls/Form';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import withRouter from '../../Toolkit/WithRouter';
import {FieldProps, FormikHelpers} from 'formik';
import * as Yup from 'yup';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import {CreateRequestRequest, RequestClient, UpdateRequestRequest} from '../../Api/RequestClients';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import {MNullable, toErrorMessage} from '../../Toolkit/Utils';
import {RequestReply, UpdateRequestValidator} from '../../Api/ControllerTypes/RequestTypes';
import MFormFieldControl from '../../Toolkit/Components/Controls/FormFieldControl';
import MTextBox from '../../Toolkit/Components/Controls/TextBox';
import {Request} from '../../Api/ControllerTypes/RequestTypes';
import MNumericBox from '../../Components/Toolkit/NumericBox';
import MTextArea from '../../Toolkit/Components/Controls/TextArea';
import MDropDown from '../../Toolkit/Components/Controls/DropDown';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import MButton from '../../Toolkit/Components/Controls/Button';
import NoteListControl from '../../Components/NoteListControl';
import AuditLogControl from '../../Components/AuditLogControl';
import FileListControl from '../../Components/FileListControl';
import MCalendar from '../../Components/Toolkit/Calendar';
import JobCrewsControl from '../../Components/JobCrewsControl';
import {CreateLockRequest, LockClient} from '../../Api/LockClient';
import SafeDigAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import InvoiceControl from '../../Components/InvoiceControl';
import {CrewContactData, WorkMailMultiReply} from '../../Api/ControllerTypes/WorkMailTypes';
import {SendJobRequest, WorkMailClient} from '../../Api/WorkMailClient';
import {MBackIconName, MSaveIconName, MSuccessIcon} from '../../Toolkit/Icons';
import EmailConfirmDialog from '../../Dialogs/EmailConfirmDialog';
import MessageDialog from '../../Dialogs/MessageDialog';
import {Divider} from 'primereact/divider';
import OkCancelDialog from '../../Dialogs/OkCancelDialog';
import DrawingControl from '../../Components/DrawingControl';
import MCalendarMulti from '../../Components/Toolkit/CalendarMulti';
import {AssignCrewRequest, JobAssignmentClient} from '../../Api/JobAssignmentClient';
import FileSelectionDialog from '../../Dialogs/FileSelectionDialog';
import {FileData, FileMultiReply} from '../../Api/ControllerTypes/FileTypes';
import CrewSelectionDialog from '../../Dialogs/CrewSelectionDialog';
import {CrewContact} from '../../Api/ControllerTypes/CrewTypes';
import MAutoComplete from '../../Components/Toolkit/AutoComplete';
import CancellationDialog from '../../Dialogs/CancellationDialog';
import {NoteReply} from '../../Api/ControllerTypes/NoteTypes';
import {CreateNoteRequest, NoteClient} from '../../Api/NoteClient';
import Button from '../../Toolkit/Components/Controls/Button';
import EnumContext from '../../Context/enum-context';
import {EnumerationClient} from '../../Api/EnumerationClient';
import {Enumeration, JobStatusReply} from '../../Api/ControllerTypes/EnumerationTypes';
import MDropDownMulti from '../../Components/Toolkit/DropDownMulti';
import {FileClient} from '../../Api/FileClient';
import { LocationClient } from '../../Api/LocationClient';
import {LocationReply} from '../../Api/ControllerTypes/LocationTypes';
import MGoogleMap from '../../Components/Toolkit/GoogleMap';
import {JobAssignmentCount} from '../../Api/ControllerTypes/JobAssignmentTypes';

//endregion

/** */
type RouteParams = {
    id: string;
};

type BookingData = {
    request: Request;
    workStartTime: MNullable<Date>;
    workEndTime: MNullable<Date>;
};

/** */
enum PageActions {
    None = 0,
    Saving = 1 << 1,
    Refreshing = 1 << 2
}

/** */
type Props = WithMsalProps & WithRouterProps<RouteParams> & {}

/** */
type State = {
    pageTitle: string;
    pageSubtitle: string;
    page: MPageProps;
    form: {
        id: string;
        data: BookingData;
        validationSchema: any;
    };
    pageActions: PageActions;
    jobStatuses: Enumeration[];
    map: {
        lat: number;
        lng: number;
        zoom: number;
        showMarker: boolean;
    };
}

/** */
class EditBookingPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private quoteValidationSchema: any = Yup.object().shape({
        request: Yup.object().shape({
            exchange: UpdateRequestValidator.exchange,
            slaType: UpdateRequestValidator.slaType,
            projectValue: UpdateRequestValidator.projectValue,
            workDescription: UpdateRequestValidator.workDescription,
            jobEndDate: UpdateRequestValidator.jobEndDate,
            selectedDays: UpdateRequestValidator.selectedDays,
            estimatedDays: UpdateRequestValidator.estimatedDays,
            address1: UpdateRequestValidator.address1,
            address2: UpdateRequestValidator.address2,
            address3: UpdateRequestValidator.address3,
            city: UpdateRequestValidator.city,
            county: UpdateRequestValidator.county,
            postcode: UpdateRequestValidator.postcode,
            northing: UpdateRequestValidator.northing,
            easting: UpdateRequestValidator.easting,
            gridReference: UpdateRequestValidator.gridReference,
            addRecipients: UpdateRequestValidator.addRecipient,
            quoteValue: UpdateRequestValidator.quoteValue,
            jobType: UpdateRequestValidator.jobType,
            invoiceNumber: UpdateRequestValidator.invoiceNumber,
            noParkingRequired: UpdateRequestValidator.noParkingRequired,
            busStop: UpdateRequestValidator.busStop
        }),
        workStartTime: UpdateRequestValidator.workStartTime,
        workEndTime: UpdateRequestValidator.workEndTime
    });

    private readonly m_routeParams: RouteParams;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_formRef: RefObject<MForm<BookingData>>;
    private readonly m_jobCrewRef: RefObject<JobCrewsControl>;
    private readonly m_noteListRef: RefObject<NoteListControl>;
    private readonly m_emailConfirmRef: RefObject<EmailConfirmDialog>;
    private readonly m_sendSubFileSelectRef: RefObject<FileSelectionDialog>;
    private readonly m_workdayFileSelectRef: RefObject<FileSelectionDialog>;
    private readonly m_crewSelectRef: RefObject<CrewSelectionDialog>;
    private readonly m_requestClient: NSwagAdapter<RequestClient>;
    private readonly m_workMailClient: NSwagAdapter<WorkMailClient>;
    private readonly m_lockClient: NSwagAdapter<LockClient>;
    private readonly m_jobAssignClient: NSwagAdapter<JobAssignmentClient>;
    private readonly m_noteClient: NSwagAdapter<NoteClient>;
    private readonly m_enumerationClient: NSwagAdapter<EnumerationClient>;
    private readonly m_fileClient: NSwagAdapter<FileClient>;
    private readonly m_locationClient: NSwagAdapter<LocationClient>;
    private readonly m_okCancelDialogRef: RefObject<OkCancelDialog>;
    private readonly m_messageDialogRef: RefObject<MessageDialog>;
    private readonly m_cancelDialogRef: RefObject<CancellationDialog>;
    private m_locationValue: string;
    private m_lockDate: Date;
    private m_interval: any = 0;
    private m_noParkingInstall: Date | undefined;

    private m_jobSetAsSendToSubContractor: boolean;
    private m_selectedFiles: FileData[];
    private m_selectedEmails: string[];
    private m_selectedCrews: CrewContactData | undefined;
    
    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_routeParams = this.props.match.params;
        this.m_pageRef = createRef<MPage>();
        this.m_formRef = createRef<MForm<BookingData>>();
        this.m_jobCrewRef = createRef<JobCrewsControl>();
        this.m_noteListRef = createRef<NoteListControl>();
        this.m_sendSubFileSelectRef = createRef<FileSelectionDialog>();
        this.m_workdayFileSelectRef = createRef<FileSelectionDialog>();
        this.m_crewSelectRef = createRef<CrewSelectionDialog>();
        this.m_emailConfirmRef = createRef<EmailConfirmDialog>();
        this.m_messageDialogRef = createRef<MessageDialog>();
        this.m_cancelDialogRef = createRef<CancellationDialog>();
        this.m_locationValue = '';
        this.m_lockDate = new Date();
        this.m_requestClient = new NSwagAdapter<RequestClient>(new RequestClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_lockClient = new NSwagAdapter<LockClient>(new LockClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_workMailClient = new NSwagAdapter<WorkMailClient>(new WorkMailClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_jobAssignClient = new NSwagAdapter<JobAssignmentClient>(new JobAssignmentClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_noteClient = new NSwagAdapter<NoteClient>(new NoteClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_enumerationClient = new NSwagAdapter<EnumerationClient>(new EnumerationClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_fileClient = new NSwagAdapter<FileClient>(new FileClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_locationClient = new NSwagAdapter<LocationClient>(new LocationClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_okCancelDialogRef = createRef<OkCancelDialog>();
        this.m_jobSetAsSendToSubContractor = false;
        this.m_selectedFiles = [];
        this.m_selectedEmails = [];
        this.m_selectedCrews = undefined;

        this.initializeState();

    }

    /** */
    private bindMethods(): void {

        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.close_Click = this.close_Click.bind(this);
        this.form_onBeforeSubmit = this.form_onBeforeSubmit.bind(this);
        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.form_onAfterSubmit = this.form_onAfterSubmit.bind(this);
        this.form_onError = this.form_onError.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.loadPageData = this.loadPageData.bind(this);
        this.toolbar_onRefreshClick = this.toolbar_onRefreshClick.bind(this);
        this.isNavigateDisabled = this.isNavigateDisabled.bind(this);
        this.isQuoteDisabled = this.isQuoteDisabled.bind(this);
        this.isJobDisabled = this.isJobDisabled.bind(this);
        this.isEmailSendDisabled = this.isEmailSendDisabled.bind(this);
        this.isPlanner = this.isPlanner.bind(this);
        this.isStatusChangeDisabled = this.isStatusChangeDisabled.bind(this);
        this.onNavigate_Click = this.onNavigate_Click.bind(this);
        this.quoteStatus_Change = this.quoteStatus_Change.bind(this);
        this.jobStatus_Change = this.jobStatus_Change.bind(this);
        this.onSelectCrew = this.onSelectCrew.bind(this);
        this.onSelectCrewDay = this.onSelectCrewDay.bind(this);
        this.onNoParkingDateSelected = this.onNoParkingDateSelected.bind(this);
        this.subJobNavigate = this.subJobNavigate.bind(this);
        this.clearLock = this.clearLock.bind(this);
        this.onUnload = this.onUnload.bind(this);
        this.refreshToken = this.refreshToken.bind(this);
        this.email_Click = this.email_Click.bind(this);
        this.toolbar_onSaveClick = this.toolbar_onSaveClick.bind(this);
        this.selectEmailComplete = this.selectEmailComplete.bind(this);
        this.selectedDaysTemplate = this.selectedDaysTemplate.bind(this);
        this.toolbar_onBackClick = this.toolbar_onBackClick.bind(this);
        this.dialog_OkClick = this.dialog_OkClick.bind(this);
        this.fileSelectionChange = this.fileSelectionChange.bind(this);
        this.onSendToSubComplete = this.onSendToSubComplete.bind(this);
        this.onSendToSubCrewSelectClose = this.onSendToSubCrewSelectClose.bind(this);
        this.crewSelectionChange = this.crewSelectionChange.bind(this);
        this.onStartTimeChange = this.onStartTimeChange.bind(this);
        this.onStartDateChange = this.onStartDateChange.bind(this);
        this.onEndDateChange = this.onEndDateChange.bind(this);
        this.onSelectAllDays = this.onSelectAllDays.bind(this);
        this.onWorkdayFileSelectionComplete = this.onWorkdayFileSelectionComplete.bind(this);
    }

    /** */
    private initializeState(): void {
        Yup.addMethod(Yup.string, 'checkDuplicate', function validateEstimate() {
            return this.test('estimate-duplicate', 'Estimate must be unique', function(value) {
                return new Promise((resolve, reject) => {
                    if(value === 'Test') {
                        reject('Estimate must be unique');
                    }
                    resolve(true);
                });
            });
        });
        //  Page
        const page: MPageProps = {locked: false};

        const data: Request = {
            id: '',
            estimate: '',
            additionalRef: '',
            locked: false,
            requestor: '',
            exchange: '',
            slaType: 0,
            projectValue: 0,
            quoteStatus: 0,
            quoteCompleteDate: null,
            workStream: 3,
            costClaimable: 0,
            quoteValue: 0,
            jobPriority: -1,
            jobType: [],
            jobDetail: '',
            jobStatus: 0,
            workDescription: '',
            estimatedDays: 0,
            address1: '',
            address2: '',
            address3: '',
            city: '',
            county: '',
            postcode: '',
            northing: 0,
            easting: 0,
            gridReference: '',
            dueDate: new Date(),
            slipNumber: '',
            jobStartDate: null,
            endType: 0,
            jobEndDate: null,
            selectedDays: [],
            noParkingRequired: false,
            weekendWorks: false,
            busStop: -1,
            dayOnly: false,
            civilsName: '',
            civilsEmail: '',
            civilsPhone: '',
            siteDocsComplete: false,
            invoiceNumber: '',
            parent: '',
            createdOn: null,
            addRecipients: '',
            permitRef: '',
            permitStatus: -1,
            drawingRef: '',
            resources: [],
            commercialStatus: 0,
            externalInvoicesReceived: 0,
            jobCompletedDate: null,
            commercialCompleteDate: null,
            dfeStatus: 0,
            worksTotalCost: 0,
            uplift: 0,
            dfeValue: 0,
            dfePartialReason: 0,
            dfePartialInfo: '',
            sentToDepotnet: false,
            actualCost: 0,
            drawingsBy: []
        };
        
        this.state = {
            pageTitle: '',
            pageSubtitle: '',
            page: page,
            form: {
                id: 'bookingForm',
                data: {request: data, workStartTime: null, workEndTime: null},
                validationSchema: this.quoteValidationSchema,
            },
            pageActions: PageActions.None,
            jobStatuses: [],
            map: {
                lat: 53.4591,
                lng: -2.8571,
                zoom: 18,
                showMarker: false
            }
        };

    }

    public componentDidMount(): void {
        window.addEventListener("unload", this.onUnload);
        this.m_interval = setInterval(this.refreshToken, 1000);
    }

    public componentWillUnmount(): void {
        clearInterval(this.m_interval);
        this.clearLock();
    }

    public async refreshToken(): Promise<void> {
        //refresh lock Token
        if((Date.now() - this.m_lockDate.getTime()) > 5000)
        {
            await this.m_lockClient.call(
                (client) => client.create(new CreateLockRequest(
                    {
                        itemId: this.state.form.data.request.id
                    })
                ));
            this.m_lockDate = new Date();
        }
    }

    private async onUnload(): Promise<void> {
        await this.clearLock();
    }

    private async clearLock(): Promise<void> {
        if(this.state.form.data.request.id !== ''){
            await this.m_lockClient.call(client => client.delete(new CreateLockRequest(
                {
                    itemId: this.state.form.data.request.id
                })
            ));
        }
    }

    private form_onBeforeSubmit(fieldValues: BookingData, formikHelpers: FormikHelpers<BookingData>): boolean {
        this.safeSetState(draft => draft.pageActions !== PageActions.Saving);
        return false;
    }

    private async form_onSubmit(fieldValues: BookingData, formikHelpers: FormikHelpers<BookingData>): Promise<void> {
        const startTime = new Date(fieldValues.request.jobStartDate!);
        startTime.setHours(fieldValues.workStartTime?.getHours() ?? 7);
        startTime.setMinutes(fieldValues.workStartTime?.getMinutes() ?? 0);
        let endTime: MNullable<Date>;
        if(fieldValues.request.endType === 0) {
            endTime = new Date(fieldValues.request.jobEndDate!);
            endTime.setHours(fieldValues.workEndTime?.getHours() ?? 17);
            endTime.setMinutes(fieldValues.workEndTime?.getMinutes() ?? 0);
        } else {
            endTime = null;
        }


        const updateRequest = new UpdateRequestRequest({
            id: fieldValues.request.id,
            estimate: fieldValues.request.estimate,
            additionalRef: fieldValues.request.additionalRef,
            exchange: fieldValues.request.exchange,
            slaType: fieldValues.request.slaType,
            projectValue: fieldValues.request.projectValue,
            quoteStatus: fieldValues.request.quoteStatus,
            workStream: fieldValues.request.workStream,
            costClaimable: fieldValues.request.costClaimable,
            quoteValue: fieldValues.request.quoteValue,
            jobPriority: fieldValues.request.jobPriority,
            jobType: fieldValues.request.jobType,
            jobDetail: fieldValues.request.jobDetail,
            workDescription: fieldValues.request.workDescription,
            estimatedDays: fieldValues.request.estimatedDays,
            address1: fieldValues.request.address1,
            address2: fieldValues.request.address2,
            address3: fieldValues.request.address3,
            city: fieldValues.request.city,
            county: fieldValues.request.county,
            postcode: fieldValues.request.postcode,
            northing: fieldValues.request.northing,
            easting: fieldValues.request.easting,
            gridReference: fieldValues.request.gridReference,
            dueDate: fieldValues.request.dueDate ?? new Date(),
            slipNumber: fieldValues.request.slipNumber,
            jobStartDate: startTime ?? undefined,
            endType: fieldValues.request.endType ?? 0,
            jobEndDate: endTime ?? undefined,
            selectedDays: fieldValues.request.selectedDays ?? undefined,
            noParkingRequired: fieldValues.request.noParkingRequired,
            weekendWorks: fieldValues.request.weekendWorks,
            busStop: fieldValues.request.busStop,
            dayOnly: fieldValues.request.dayOnly,
            civilsName: fieldValues.request.civilsName,
            civilsEmail: fieldValues.request.civilsEmail,
            civilsPhone: fieldValues.request.civilsPhone,
            siteDocsComplete: fieldValues.request.siteDocsComplete,
            invoiceNumber: fieldValues.request.invoiceNumber,
            jobStatus: fieldValues.request.jobStatus,
            addRecipients: fieldValues.request.addRecipients?.trim().toLowerCase() ?? '',
            permitRef: fieldValues.request.permitRef,
            permitStatus: fieldValues.request.permitStatus,
            sentToDepotnet: fieldValues.request.sentToDepotnet
        });

        const result = await this.m_requestClient.callWithReply(RequestReply, (client => client.update(updateRequest)));

        result.throwIfFailed()

        //Clear dirty flag.
        formikHelpers.resetForm();
    }

    private async form_onAfterSubmit(outcome: MFormOutcome, fieldValues: BookingData, formikHelpers: FormikHelpers<BookingData>, exception: any | undefined): Promise<void> {
        if(this.m_jobSetAsSendToSubContractor) {
            //Get files to send from user and email to sub contactor.
            this.m_selectedCrews = undefined;
            this.m_crewSelectRef.current!.show('Please select sub contractor for drawing request.');
        }

        if(outcome === 'success') {
            this.m_pageRef.current!.messageBar.success(`Save complete.`);
        }
        this.safeSetState(draft => draft.pageActions = PageActions.None);

        await this.loadPageData();
        await this.m_jobCrewRef.current?.refresh();
    }

    private form_onError(exception: any): boolean
    {
        const handled = false;
        this.m_pageRef.current!.messageBar.error(toErrorMessage(exception));
        return handled;
    }

    private async loadPageData(): Promise<void> {
        const id = this.props.match.params.id;

        const jobStatusResult = await this.m_enumerationClient.callWithReply(JobStatusReply,
            (client) => client.getJobStatus(id));
        jobStatusResult.throwIfFailed();

        //Pull request data
        const result = await this.m_requestClient.callWithReply(RequestReply, (client) => client.get(id));
        result.throwIfFailed();

        result.reply!.request!.costClaimable = result.reply!.request!.costClaimable ?? 0;
        result.reply!.request!.jobType = result.reply!.request!.jobType ?? 0;
        result.reply!.request!.gridReference = result.reply!.request!.gridReference ?? '';
        result.reply!.request!.siteDocsComplete = result.reply!.request!.siteDocsComplete ?? false;

        //Get location data
        const easting = result.reply?.request?.easting ?? 0;
        const northing = result.reply?.request?.northing ?? 0;
        const location = await this.m_locationClient.callWithReply(LocationReply,
            (client) => client.bngToLatLong(easting, northing))
        location.throwIfFailed();

        this.safeSetState((draft: State) => {

            draft.pageTitle = 'Edit Booking';
            draft.pageSubtitle = `Edit Booking: ${result.reply!.request!.estimate}`;
            draft.form.data.request = result.reply!.request!;
            draft.form.data.workStartTime = result.reply!.request!.jobStartDate;
            draft.form.data.workEndTime = result.reply!.request!.jobEndDate;
            draft.jobStatuses = jobStatusResult.reply!.jobStatuses;

            draft.map.lat = location.reply!.location!.lat;
            draft.map.lng = location.reply!.location!.long;
            draft.map.showMarker = true;
        });
        this.m_locationValue = result.reply!.request!.gridReference;
    }

    private toolbar_onBackClick(): void {
        if(this.m_formRef.current?.getIsDirty()) {
            this.m_okCancelDialogRef.current!.show(
                'Data Changed',
                `Data has been changed but not saved. Do you still wish to exit?`,
                MSuccessIcon
            );
            return;
        }

        this.props.history.goBack();
    }

    private dialog_OkClick(): void {
        this.props.history.goBack();
    }

    /** */
    private async toolbar_onRefreshClick(): Promise<void> {

        this.safeSetState((draft: State) => {
            draft.pageActions |= PageActions.Refreshing;
        });

        try {
            await this.loadPageData();
        } finally {

            this.safeSetState((draft: State) => {
                draft.pageActions &= ~PageActions.Refreshing;
            });
        }
    }

    private async toolbar_onSaveClick(): Promise<void> {
        await this.m_formRef.current!.submit();
        if(!(await this.m_formRef.current!.validate())) {
            this.m_messageDialogRef.current!.show("Validation Error",
                "The form data is invalid. Please correct any field (red) errors before saving.");
        }
    }

    private async page_onLoadData(): Promise<void> {
        return this.loadPageData()
    }

    /** */
    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MButton label="Back"
                         color="secondary"
                         iconOnly={true}
                         icon={MBackIconName}
                         tooltip="Go Back"
                         disabled={false}
                         onClick={this.toolbar_onBackClick}/>
                <MSpacer/>
                <MSpacer/>
                <MButton label="Save"
                         color="success"
                         iconOnly={true}
                         icon={MSaveIconName}
                         tooltip="Save"
                         disabled={this.isFormDisabled}
                         isBusy={this.isSaving}
                         onClick={this.toolbar_onSaveClick}/>
                <MSpacer/>
                <MButton label="Send Work Mails"
                         color="warning"
                         icon={"pi pi-send"}
                         iconOnly={true}
                         disabled={this.isEmailSendDisabled}
                         onClick={this.email_Click}
                />
            </MToolbar>
        );

    }

    //***************************************************************************************************************
    // Send Workday email
    //***************************************************************************************************************
    private async email_Click(): Promise<void> {
        const crewReply = await this.m_workMailClient.callWithReply(
            WorkMailMultiReply, client => client.listCrewForJob(this.state.form.data.request.id));
        crewReply.throwIfFailed();

        const userName = this.props.msalContext.accounts[0].username;

        const emailAddresses: string[] = [];
        if(crewReply.reply!.crews.length > 0) {
            for(let crew of crewReply.reply!.crews) {
                if(crew.crewContacts.length === 0) {
                    //no email contacts for crew so send to logged in user.
                    emailAddresses.push(userName);
                } else {
                    emailAddresses.push(...crew.crewContacts.map(cc => cc.contact))
                }
            }
        } else {
            //No crews assigned so send to logged in user.
            emailAddresses.push(userName);
        }
        const uniqueAddresses = emailAddresses.filter((item, i, ar) => ar.indexOf(item) === i);

        this.loadPageData();

        //Pop up to confirm send.
        this.m_emailConfirmRef.current!.show(crewReply.reply!.crews, uniqueAddresses, this.selectEmailComplete, false);
    }

    private async selectEmailComplete(crewContacts: CrewContactData[], crewContactEmails: string[]): Promise<void> {
        const userName = this.props.msalContext.accounts[0].username;

        let emails: string[] = [];
        if(crewContactEmails.length > 0) {
            emails = crewContactEmails;
        } else {
            emails = [userName];
        }
        this.m_selectedEmails = emails;
        this.m_selectedFiles = [];
        this.m_workdayFileSelectRef.current!.show(this.state.form.data.request.id, 'Please select files to send with the job data');
    }

    private async onWorkdayFileSelectionComplete(cancelled: boolean): Promise<void> {
        if(!cancelled) {
            const fileResult = await this.m_fileClient.callWithReply(
                FileMultiReply, (client) => client.getAll(this.state.form.data.request.id));
            fileResult.throwIfFailed();

            //clear unselected files.
            const fileIds = this.m_selectedFiles.map(f => f.id);
            const unselectedFiles = fileResult.reply!.files.filter(f => !fileIds.includes(f.id));
            for(let f: number = 0; f < unselectedFiles.length; f++) {
                const archiveReply = await this.m_fileClient.call(
                    (client) => client.changeSendToCrew(unselectedFiles[f].id, false));

                archiveReply.throwIfFailed();
            }

            //mark unselected files.
            for(let f: number = 0; f < this.m_selectedFiles.length; f++) {
                const archiveReply = await this.m_fileClient.call(
                    (client) => client.changeSendToCrew(this.m_selectedFiles[f].id, true));

                archiveReply.throwIfFailed();
            }

            //Send work email
            const crewReply = await this.m_workMailClient.call(
                client => client.sendSingleJob(new SendJobRequest({
                    requestId: this.props.match.params.id,
                    recipients: this.m_selectedEmails
                })));
            crewReply.throwIfFailed();

            this.m_messageDialogRef.current!.show(
                'Send Complete',
                `Email send complete`,
                MSuccessIcon);
        }
    }

    //***************************************************************************************************************
    //Send job to crew end
    //***************************************************************************************************************

    private async close_Click(): Promise<void> {
        this.props.history.goBack();
    }

    //***************************************************************************************************************
    //Send job to sub contractor
    //***************************************************************************************************************

    private onSendToSubCrewSelectClose(fieldProps: FieldProps, cancelled: boolean): void {
        if(cancelled) {
            fieldProps.form.setFieldValue('request.jobStatus', this.state.form.data.request.jobStatus);
            this.m_jobSetAsSendToSubContractor = false;
        } else {
            this.m_selectedFiles = [];
            this.m_sendSubFileSelectRef.current!.show(this.state.form.data.request.id,
                'Please select files to send with sub contractor drawing request.');
        }
    }

    private async onSendToSubComplete(fieldProps: FieldProps, cancelled: boolean): Promise<void> {
        this.m_jobSetAsSendToSubContractor = false;
        if(cancelled) {
            fieldProps.form.setFieldValue('request.jobStatus', this.state.form.data.request.jobStatus);
        } else {
            await this.m_formRef.current!.submit();
            const userName = this.props.msalContext.accounts[0].username;

            //Assigne the current job to the selected sub contractors.
            const crewIds: number[] = this.m_selectedCrews === undefined ? [] : [this.m_selectedCrews.crew.id];

            const assignRequest = new AssignCrewRequest({
                requestId: this.state.form.data.request.id,
                startDate: this.state.form.data.request.jobStartDate ?? undefined,
                endDate: (this.state.form.data.request.endType !== 1 ? this.state.form.data.request.jobEndDate : this.state.form.data.request.jobStartDate) ?? undefined,
                crewIds: crewIds,
                tmTypes: this.state.form.data.request.jobType
            });
            const result = await this.m_jobAssignClient
                                     .callWithReply(JobAssignmentCount, (client) => client.assignCrew(assignRequest))
            result.throwIfFailed();

            //Send an email to the selected addresses.
            const emailAddresses: string[] = [];
            if(this.m_selectedCrews !== undefined) {
                const filteredAddresses = this.m_selectedCrews.crewContacts
                                              .filter((cc: CrewContact) => cc.contactType === 0)
                                              .map(cc => cc.contact);
                if(filteredAddresses.length === 0) {
                    //no email contacts for crew so send to logged in user.
                    emailAddresses.push(userName);
                } else {
                    emailAddresses.push(...filteredAddresses);
                }
            } else {
                //No crews assigned so send to logged in user.
                emailAddresses.push(userName);
            }
            const uniqueAddresses = emailAddresses.filter((item, i, ar) => ar.indexOf(item) === i);

            const sendReply = await this.m_workMailClient.call(
                client => client.sendSingleJob(new SendJobRequest({
                    requestId: this.props.match.params.id,
                    recipients: uniqueAddresses,
                    customMessage: 'Job attached. Please inform us immediately if the works are completed before the last day of the permit. ' +
                        'Please confirm you can cover the works and provide contact details for who will attend site.',
                    customAttachments: this.m_selectedFiles.map(f => f.id),
                    useCustomAttachments: true
                })));
            sendReply.throwIfFailed();

            this.loadPageData();
        }
    }

    //***************************************************************************************************************
    //Send job to sub contractor end
    //***************************************************************************************************************

    private crewSelectionChange(newSelection: CrewContactData | CrewContactData[] | undefined) {
        if(Array.isArray(newSelection)) {
            this.m_selectedCrews = newSelection[0];
        } else {
            this.m_selectedCrews = newSelection;
        }
    }

    private async quoteStatus_Change(newValue: MNullable<number>): Promise<void> {
        if(newValue === 2) { //On Hold
            this.m_noteListRef.current?.requestNote('Please enter information requirement.', true);
        } else if(newValue === 5) { //Cancelled
            this.m_noteListRef.current?.requestNote('Please enter cancellation reason.', true);
        }
    }

    private async jobStatus_Change(newValue: MNullable<number>): Promise<void> {
        if(newValue === 9) { //Cancelled
            this.m_cancelDialogRef.current?.show();
        }

        //Job set as send to contractor
        this.m_jobSetAsSendToSubContractor = newValue === 12 && this.state.form.data.request.jobStatus !== 12;
    }

    private fileSelectionChange(newSelection: FileData[]) {
        this.m_selectedFiles = newSelection;
    }

    private onDfeStatusChange(fieldProps: FieldProps, newValue: MNullable<number>): void {
        if(newValue !== 7){
            fieldProps.form.setFieldValue('request.dfePartialReason', -1);
            fieldProps.form.setFieldValue('request.dfePartialInfo', '');
        }
    }

    private isNavigateDisabled(): boolean{
        if(this.m_locationValue !== null){
            return !this.m_locationValue.match(/^(http(s):\/\/.)[-a-zA-Z0-9@:%._~#=]{2,256}\.[a-z]{2,6}([-a-zA-Z0-9@:%_,.~#?&|//=]*)$/g);
        }

        return true;
    }

    private isQuoteDisabled(): boolean {
        const allowedRoles: string[] = ['Quotation', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return this.state.form.data.request.quoteStatus === 3
            || !access
            || this.isFormDisabled;
    }

    private isJobDisabled(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access
            || this.isFormDisabled;
    }

    private isEmailSendDisabled(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access
            || this.isFormDisabled;
    }

    private isPlanner(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access;
    }

    private isStatusChangeDisabled(): boolean {
        return this.isFormDisabled;
    }

    private onNavigate_Click(): void {
        window.open(this.m_locationValue, '_blank', 'noopener,noreferrer');
    }

    private async onSelectCrew(): Promise<void> {
        const requestId = encodeURIComponent(this.state.form.data.request.id);
        const startString = encodeURIComponent(this.state.form.data.request.jobStartDate!.toISOString());
        const endString = this.state.form.data.request.jobEndDate !== null && this.state.form.data.request.jobEndDate !== undefined
                          ? encodeURIComponent(this.state.form.data.request.jobEndDate!.toISOString())
                          : encodeURIComponent(this.state.form.data.request.jobStartDate!.toISOString());

        if(await this.m_formRef.current!.validate()) {
            await this.m_formRef.current!.submit();
            this.props.navigate(`/crews?requestId=${requestId}&startDate=${startString}&endDate=${endString}`);
        } else {
            this.m_messageDialogRef.current!.show("Validation Error",
                "The form data is invalid and must be corrected before assigning a crew.");
        }
    }

    private async onSelectCrewDay(date: Date, tmType: number[]): Promise<void> {
        const requestId = encodeURIComponent(this.state.form.data.request.id);
        const dateString = encodeURIComponent(date.toISOString());
        const tmTypeString = encodeURIComponent(tmType.join(","))

        if(await this.m_formRef.current!.validate()) {
            await this.m_formRef.current!.submit();
            this.props.navigate(`/crews?requestId=${requestId}&startDate=${dateString}&endDate=${dateString}&tmTypes=${tmTypeString}`);
        } else {
            this.m_messageDialogRef.current!.show("Validation Error",
                "The form data is invalid and must be corrected before assigning a crew. Check all fields to see highlighted errors.");
        }
    }

    private onSelectedDaysChange(fieldProps: FieldProps, selectedDays: Date[] | undefined) {
        let weekendWorks: boolean = false;
        selectedDays?.forEach(d => {
            if(d.getDay() === 0 || d.getDay() === 6) { weekendWorks = true; }
        });

        if(selectedDays !== null){
            selectedDays = selectedDays!.sort((A, B) => A < B ? -1 : A > B ? 1 : 0);
        }

        fieldProps.form.setFieldValue('request.selectedDays', selectedDays);
        fieldProps.form.setFieldValue('request.weekendWorks', weekendWorks);
    }

    private onSelectAllDays(fieldProps: FieldProps): void {
        let selectedDays: Date[] = [];
        let currentDate = new Date(fieldProps.form.values.request.jobStartDate);
        const lastDate = new Date(fieldProps.form.values.request.jobEndDate);

        while(currentDate <= lastDate) {
            selectedDays.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        fieldProps.form.setFieldValue('request.selectedDays', selectedDays);
    }

    private onStartDateChange(fieldProps: FieldProps, newDate: Date | undefined) {
        //filter days so any outside the new selection can be removed.
        let searchDate = new Date(newDate!);
        searchDate!.setHours(0, 0, 0, 0);
        const filteredDays = (fieldProps.form.values.request.selectedDays as Date[]).filter(d => d >= newDate!);

        fieldProps.form.setFieldValue('request.jobStartDate', newDate!);
        fieldProps.form.setFieldValue('request.selectedDays', filteredDays.sort((A, B) => A < B ? -1 : A > B ? 1 : 0));
    }

    private onStartTimeChange(fieldProps: FieldProps, newDate: Date | undefined) {
        let daysSelected = false;
        if((newDate?.getHours() ?? 0) >= 6 && (newDate?.getHours() ?? 0) < 18) {
            daysSelected = true;
        }

        fieldProps.form.setFieldValue('request.dayOnly', daysSelected);
    }

    private onEndDateChange(fieldProps: FieldProps, newDate: Date | undefined) {
        //filter days so any outside the new selection can be removed.
        let searchDate = new Date(newDate!);
        searchDate!.setHours(23, 59, 59, 999);
        const filteredDays = (fieldProps.form.values.request.selectedDays as Date[]).filter(d => d <= newDate!);

        fieldProps.form.setFieldValue('request.jobEndDate', newDate!);
        fieldProps.form.setFieldValue('request.selectedDays', filteredDays.sort((A, B) => A < B ? -1 : A > B ? 1 : 0));
    }

    private onNoParkingDateSelected(fieldProps: FieldProps,  selectedDate: Date | undefined) {
        this.m_noParkingInstall = selectedDate;

        //Reset the no parking selection if no date is selected.
        if(selectedDate === undefined) {
            fieldProps.form.setFieldValue('request.noParkingRequired', false);
        }
    }

    private async subJobNavigate(newJob: boolean, id: string = '', subJobCount: number = 0): Promise<void> {
        const subCountText = (Number(subJobCount) + 1).toString().padStart(2, '0');
        if(newJob) {
            const createRequest = new CreateRequestRequest({
                estimate: this.state.form.data.request.estimate + `-S${subCountText}`,
                additionalRef: '',
                requestor: this.props.msalContext.accounts[0].name,
                exchange: this.state.form.data.request.exchange,
                slaType: this.state.form.data.request.slaType,
                projectValue: this.state.form.data.request.projectValue,
                workStream: this.state.form.data.request.workStream,
                workDescription: this.state.form.data.request.workDescription,
                estimatedDays: 0,
                address1: this.state.form.data.request.address1,
                address2: this.state.form.data.request.address2,
                address3: this.state.form.data.request.address3,
                city: this.state.form.data.request.city,
                county: this.state.form.data.request.county,
                postcode: this.state.form.data.request.postcode,
                northing: this.state.form.data.request.northing,
                easting: this.state.form.data.request.easting,
                gridReference: this.state.form.data.request.gridReference,
                parent: this.state.form.data.request.id
            });

            const result = await this.m_requestClient.callWithReply(RequestReply, (client => client.create(createRequest)));
            result.throwIfFailed();

            this.props.navigate(`/requests/create/${result.reply!.request!.id}`);
        } else {
            this.props.navigate(`/requests/${id}`);
            window.location.reload();
        }
    }

    private async cancelDialogClose(fieldProps: FieldProps, noteData: string, cancel: boolean): Promise<void> {
        if(cancel){
            //Restore the old status value;
            fieldProps.form.setFieldValue('request.jobStatus', this.state.form.data.request.jobStatus);
        } else {
            const createResult = await this.m_noteClient.callWithReply(
                NoteReply, (client) => client.create(new CreateNoteRequest({
                    requestId: this.state.form.data.request.id,
                    noteText: noteData
                })));
            createResult.throwIfFailed();
            await this.m_noteListRef.current!.refresh();
        }
    }

    private selectedDaysTemplate(): string{
        const selectedDays = this.state.form.data.request.selectedDays;
        const dayArray: string[] = [];

        selectedDays.forEach(d => {
            const dayName = d.toLocaleDateString('en-GB', { weekday: 'short' });
            if(!dayArray.includes(dayName)) {
                dayArray.push(dayName);
            }
        });

        return dayArray.join(', ');
    }

    /** */
    public render(): ReactElement {

        const yesNoList: MDropDownItem<boolean>[] = [{label: 'No', value: false},{label: 'Yes', value: true}];
        const shiftPatternList: MDropDownItem<boolean>[] = [{label: 'Nights', value: false},{label: 'Days', value: true}];

        return (
            <MPage title={this.state.pageTitle}
                   subtitle={this.state.pageSubtitle}
                   locked={this.state.page.locked}
                   width={12}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <OkCancelDialog ref={this.m_okCancelDialogRef}
                                okClicked={this.dialog_OkClick}
                />
                <EmailConfirmDialog ref={this.m_emailConfirmRef}/>
                <MessageDialog ref={this.m_messageDialogRef}/>

                <div style={{position: "fixed", left: 0, width: "66%", top: "200px", padding: "0 2rem"}}>
                    <div style={{overflowY: "scroll", overflowX: "hidden", maxHeight: 'calc(100vh - 260px)'}}>
                        <MForm formId={this.state.form.id}
                               initialValues={this.state.form.data}
                               validationSchema={this.state.form.validationSchema}
                               onBeforeSubmit={this.form_onBeforeSubmit}
                               onSubmit={this.form_onSubmit}
                               onAfterSubmit={this.form_onAfterSubmit}
                               onError={this.form_onError}
                               ref={this.m_formRef}
                        >

                            <div className="formgrid grid">
                                <div className="col-12">

                                    <MFormSection title="General">
                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Estimate"
                                                               fieldName="request.estimate">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.EstimateMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={true}
                                                                  className={fieldClasses}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Requestor"
                                                               fieldName="request.requestor">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.RequestorMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={true}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Exchange"
                                                               fieldName="request.exchange">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MAutoComplete id={fieldProps.field.name}
                                                                       fieldProps={fieldProps}
                                                                       disabled={false || this.isPlanner()}
                                                                       className={fieldClasses}
                                                                       suggestions={this.context?.enums === null ? [] : this.context?.enums.exchangeList.map(e => e.name)}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Additional Ref"
                                                               fieldName="request.additionalRef">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.AdditionalRefMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled || this.isPlanner()}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Project Code"
                                                               fieldName="request.projectValue">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDown<number> id={fieldProps.field.name}
                                                                           disabled={this.isFormDisabled || this.isPlanner()}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.projectList.map(project => ({
                                                                                   value: project.id,
                                                                                   label: project.code + ' - ' + project.name
                                                                               }))}
                                                                           initialValue={0}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Type Of Works"
                                                               fieldName="request.workStream">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDown<number> id={fieldProps.field.name}
                                                                           disabled={this.isFormDisabled || this.isPlanner()}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.workStreams.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={0}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label="Estimated Days"
                                                               fieldName="request.estimatedDays">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={5}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isQuoteDisabled || this.isPlanner()}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-3">
                                            <MFormFieldControl label={'SLA'}
                                                               fieldName={'request.slaType'}>
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MDropDown<number> id="ProjectSelection"
                                                                           disabled={true}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.slaTypes.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={0}
                                                        />
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-12">
                                            <MFormFieldControl label="Work Description (please provide detail if no A55/Job pack is provided)"
                                                               fieldName="request.workDescription">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextArea id={fieldProps.field.name}
                                                                  maxLength={Request.WorkDescriptionMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled || this.isPlanner()}
                                                                  className={fieldClasses}
                                                                   rows={5}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-12">
                                            <MFormFieldControl label="Additional E-mail addresses"
                                                               fieldName={'request.addRecipients'}>

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextArea id={'addresses'}
                                                                   maxLength={Request.AddRecipientsMaxLength}
                                                                   readonly={false}
                                                                   fieldProps={fieldProps}
                                                                   disabled={this.isFormDisabled || this.isPlanner()}
                                                                   className={fieldClasses}
                                                                   rows={5}
                                                        />
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Quotation">

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Status"
                                                               fieldName="request.quoteStatus">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDown<number> id={fieldProps.field.name}
                                                                           disabled={this.isQuoteDisabled || this.isPlanner()}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.quoteStatuses.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={0}
                                                                           onChange={this.quoteStatus_Change}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Cost Claimable"
                                                               fieldName="request.costClaimable">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDown<number> id={fieldProps.field.name}
                                                                           disabled={this.isQuoteDisabled || this.isPlanner()}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.costTypes.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={0}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Quote Value"
                                                               fieldName="request.quoteValue">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MNumericBox id={fieldProps.field.name}
                                                                     maxLength={9}
                                                                     fieldProps={fieldProps}
                                                                     disabled={this.isQuoteDisabled || this.isPlanner()}
                                                                     className={fieldClasses}
                                                                     decimalPlaces={2}
                                                                     mode={'currency'}
                                                                     currency={'GBP'}
                                                                     useGrouping={false}/>
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-12">
                                            <MFormFieldControl label="Job Types"
                                                               fieldName="request.jobType">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDownMulti<number> id={fieldProps.field.name}
                                                                                disabled={this.isQuoteDisabled || this.isPlanner()}
                                                                                fieldProps={fieldProps}
                                                                                items={this.context?.enums === null ? [] : this.context?.enums.jobTypes.map((enumvalue) => {
                                                                                    return {label: enumvalue.name, value: enumvalue.id};
                                                                                })}
                                                                                initialValue={[]}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-12">
                                            <MFormFieldControl label="TM Detail"
                                                               fieldName="request.jobDetail">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.JobDetailMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled || this.isPlanner()}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                    </MFormSection>

                                    <MFormSection title="Commercial">

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Commercial Status"
                                                               fieldName="request.commercialStatus">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDown<number> id={fieldProps.field.name}
                                                                           disabled={true}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.commercial.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={0}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Uplift Value"
                                                               fieldName="request.uplift">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MNumericBox id={fieldProps.field.name}
                                                                     maxLength={9}
                                                                     fieldProps={fieldProps}
                                                                     disabled={true}
                                                                     className={fieldClasses}
                                                                     decimalPlaces={2}
                                                                     mode={'currency'}
                                                                     currency={'GBP'}
                                                                     useGrouping={false}/>
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Actual Cost"
                                                               fieldName="request.actualCost">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MNumericBox id={fieldProps.field.name}
                                                                     maxLength={9}
                                                                     fieldProps={fieldProps}
                                                                     disabled={true}
                                                                     className={fieldClasses}
                                                                     decimalPlaces={2}
                                                                     mode={'currency'}
                                                                     currency={'GBP'}
                                                                     useGrouping={false}/>
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="DFE Value"
                                                               fieldName="request.dfeValue">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MNumericBox id={fieldProps.field.name}
                                                                     maxLength={9}
                                                                     fieldProps={fieldProps}
                                                                     disabled={true}
                                                                     className={fieldClasses}
                                                                     decimalPlaces={2}
                                                                     mode={'currency'}
                                                                     currency={'GBP'}
                                                                     useGrouping={false}/>
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="DFE Status"
                                                               fieldName="request.dfeStatus">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDown<number> id={fieldProps.field.name}
                                                                           disabled={true}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.dfeStatuses.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={0}
                                                                           onChange={e => this.onDfeStatusChange(fieldProps, e)}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="DFE Partial Reason"
                                                               fieldName="request.dfePartialReason">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MDropDown<number> id={fieldProps.field.name}
                                                                           disabled={true}
                                                                           fieldProps={fieldProps}
                                                                           items={this.context?.enums === null ? [] : this.context?.enums.dfePartialReasons.map((enumvalue) => {
                                                                               return {label: enumvalue.name, value: enumvalue.id};
                                                                           })}
                                                                           initialValue={-1}
                                                        />
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-12">
                                            <MFormFieldControl label="DFE Partial Information"
                                                               fieldName="request.dfePartialInfo">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.AdditionalRefMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={true}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                    </MFormSection>

                                    {this.state.form.data.request.jobStatus > 0 && <Divider/>}
                                    {this.state.form.data.request.jobStatus > 0 &&
                                        <MFormSection title="Job Data">
                                            <div className="col-12 md:col-3">
                                                <MFormFieldControl label="DFE Slip Number"
                                                                   fieldName="request.slipNumber">

                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                        return (
                                                            <MTextBox id={fieldProps.field.name}
                                                                      maxLength={Request.SlipNumberMaxLength}
                                                                      readonly={false}
                                                                      fieldProps={fieldProps}
                                                                      disabled={this.isFormDisabled || this.isPlanner()}
                                                                      className={fieldClasses}/>
                                                        );

                                                    }}

                                                </MFormFieldControl>
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <MFormFieldControl label={'Priority'}
                                                                   fieldName={'request.jobPriority'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <React.Fragment>
                                                                <MDropDown<number> id={fieldProps.field.name}
                                                                                   disabled={this.isFormDisabled || this.isPlanner()}
                                                                                   fieldProps={fieldProps}
                                                                                   items={this.context?.enums === null ? [] : this.context?.enums.jobPriority.map((enumvalue) => {
                                                                                       return {label: enumvalue.name, value: enumvalue.id};
                                                                                   })}
                                                                                   className={fieldClasses}
                                                                                   initialValue={0}
                                                                />
                                                            </React.Fragment>
                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>
                                        </MFormSection>}

                                    {this.state.form.data.request.jobStatus > 0 &&
                                    <MFormSection>
                                            <div className="col-12 md:col-3">
                                                <MFormFieldControl label={'Start Date'} fieldName={'request.jobStartDate'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <MCalendar id={fieldProps.field.name}
                                                                       fieldProps={fieldProps}
                                                                       disabled={this.isFormDisabled}
                                                                       className={fieldClasses}
                                                                       showTime={false}
                                                                       showButtonBar={false}
                                                                       onChange={(newDate: Date | undefined) =>  this.onStartDateChange(fieldProps, newDate)}
                                                            />
                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>

                                            <div className="col-12 md:col-2">
                                                <MFormFieldControl label={'Work Start Time'} fieldName={'workStartTime'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <MCalendar id={fieldProps.field.name}
                                                                       fieldProps={fieldProps}
                                                                       disabled={this.isFormDisabled}
                                                                       className={fieldClasses}
                                                                       showTime={true}
                                                                       timeOnly={true}
                                                                       showButtonBar={false}
                                                                       hourFormat={'24'}
                                                                       onChange={(newDate: Date | undefined) =>  this.onStartTimeChange(fieldProps, newDate)}
                                                            />
                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>



                                            <div className="col-12 md:col-2">
                                                <MFormFieldControl label={'End Type'}
                                                                   fieldName={'request.endType'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <React.Fragment>
                                                                <MDropDown<number> id={fieldProps.field.name}
                                                                                    disabled={this.isFormDisabled}
                                                                                    fieldProps={fieldProps}
                                                                                    items={[{label: 'End Date', value: 0},{label: 'In Situ', value: 1}]}
                                                                                    className={fieldClasses}
                                                                                    initialValue={0}
                                                                />
                                                            </React.Fragment>
                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>

                                            <div className="col-12 md:col-3">
                                                <MFormFieldControl label={'End Date'} fieldName={'request.jobEndDate'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <MCalendar id={fieldProps.field.name}
                                                                       fieldProps={fieldProps}
                                                                       disabled={fieldProps.form.values.request.endType !== 0}
                                                                       className={fieldClasses}
                                                                       showTime={false}
                                                                       showButtonBar={false}
                                                                       hourFormat={'24'}
                                                                       onChange={(newDate) => this.onEndDateChange(fieldProps, newDate)}
                                                            />
                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>

                                            <div className="col-12 md:col-2">
                                                <MFormFieldControl label={'Work End Time'} fieldName={'workEndTime'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <MCalendar id={fieldProps.field.name}
                                                                       fieldProps={fieldProps}
                                                                       disabled={fieldProps.form.values.request.endType !== 0}
                                                                       className={fieldClasses}
                                                                       showTime={true}
                                                                       timeOnly={true}
                                                                       showButtonBar={false}
                                                                       hourFormat={'24'}
                                                            />
                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>

                                            <div className="col-12 md:col-12">
                                                <MFormFieldControl label={'Selected Days'} fieldName={'request.selectedDays'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <React.Fragment>
                                                                {<MCalendarMulti
                                                                    id={fieldProps.field.name}
                                                                    fieldProps={fieldProps}
                                                                    disabled={this.isFormDisabled}
                                                                    className={fieldClasses}
                                                                    minDate={fieldProps.form.values.request.jobStartDate ?? undefined}
                                                                    maxDate={fieldProps.form.values.request.jobEndDate ?? undefined}
                                                                    onChange={(newDates: Date[] | undefined) =>  this.onSelectedDaysChange(fieldProps, newDates)}
                                                                />}
                                                                <MSpacer/>
                                                                <Button
                                                                    disabled={false}
                                                                    label={'All'}
                                                                    tooltip={'Select all days in range'}
                                                                    onClick={e => this.onSelectAllDays(fieldProps)}
                                                                />
                                                            </React.Fragment>

                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>

                                            <div className="col-12 md:col-3">
                                                <MFormFieldControl label={'No Parking Cones'}
                                                                   fieldName={'request.noParkingRequired'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <React.Fragment>
                                                                <MDropDown<boolean> id='noParkingRequired'
                                                                                    disabled={true}
                                                                                    fieldProps={fieldProps}
                                                                                    items={yesNoList}
                                                                                    initialValue={false}
                                                                />
                                                            </React.Fragment>
                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>

                                            <div className="col-12 md:col-3">
                                                <MFormFieldControl label={'Bus Stop Suspension'}
                                                                   fieldName={'request.busStop'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <MDropDown<number> id='busStop'
                                                                                disabled={true}
                                                                                fieldProps={fieldProps}
                                                                                items={this.context?.enums === null ? [] : this.context?.enums.busStopValues.map((enumvalue) => {
                                                                                    return {label: enumvalue.name, value: enumvalue.id};
                                                                                })}
                                                                                initialValue={-1}
                                                            />
                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>

                                            <div className="col-12 md:col-3">
                                                <MFormFieldControl label={'Weekend Works'}
                                                                   fieldName={'request.weekendWorks'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <MDropDown<boolean> id='weekendWorks'
                                                                                disabled={true}
                                                                                fieldProps={fieldProps}
                                                                                items={yesNoList}
                                                                                initialValue={false}
                                                            />
                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>

                                            <div className="col-12 md:col-3">
                                                <MFormFieldControl label={'Shift Pattern'}
                                                                   fieldName={'request.dayOnly'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <MDropDown<boolean> id='dayOnly'
                                                                                disabled={true}
                                                                                fieldProps={fieldProps}
                                                                                items={shiftPatternList}
                                                                                initialValue={false}
                                                            />
                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>

                                            <div className="col-12 md:col-12">
                                                <MFormFieldControl label="Permit Reference(s)"
                                                                   fieldName="request.permitRef">

                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                        return (
                                                            <MTextArea id={fieldProps.field.name}
                                                                       maxLength={Request.PermitRefMaxLength}
                                                                       readonly={false || this.isPlanner()}
                                                                       fieldProps={fieldProps}
                                                                       disabled={this.isFormDisabled}
                                                                       className={fieldClasses}
                                                                       rows={3}/>
                                                        );

                                                    }}

                                                </MFormFieldControl>
                                            </div>

                                            <div className="col-12 md:col-3">
                                                <MFormFieldControl label={'Permit Status'}
                                                                   fieldName={'request.permitStatus'}>
                                                    {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                        return (
                                                            <MDropDown<number> id={fieldProps.field.name}
                                                                                disabled={this.isFormDisabled || this.isPlanner()}
                                                                                fieldProps={fieldProps}
                                                                                items={this.context?.enums === null ? [] : this.context?.enums.permitStatuses.map((enumvalue) => {
                                                                                    return {label: enumvalue.name, value: enumvalue.id};
                                                                                })}
                                                            />
                                                        );
                                                    }}
                                                </MFormFieldControl>
                                            </div>
                                        </MFormSection>}

                                    {this.state.form.data.request.jobStatus > 0 &&
                                        <MFormSection>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Civils Name"
                                                               fieldName="request.civilsName">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.CivilsNameMaxLength}
                                                                  readonly={false || this.isPlanner()}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Civils Email"
                                                               fieldName="request.civilsEmail">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.CivilsEmailMaxLength}
                                                                  readonly={false || this.isPlanner()}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Civils Phone"
                                                               fieldName="request.civilsPhone">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.CivilsPhoneMaxLength}
                                                                  readonly={false || this.isPlanner()}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Booking Status"
                                                               fieldName="request.jobStatus">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <React.Fragment>
                                                            <CancellationDialog ref={this.m_cancelDialogRef}
                                                                                onDialogClose={(data, cancel) => this.cancelDialogClose(fieldProps, data, cancel)}/>
                                                            <MDropDown<number> id={fieldProps.field.name}
                                                                               disabled={this.isStatusChangeDisabled()}
                                                                               fieldProps={fieldProps}
                                                                               items={this.state.jobStatuses.map((enumvalue) => {
                                                                                   return {label: enumvalue.name, value: enumvalue.id};
                                                                               })}
                                                                               initialValue={0}
                                                                               onChange={this.jobStatus_Change}
                                                            />
                                                        </React.Fragment>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label={'Site Docs (Required to Complete Job)'}
                                                               fieldName={'request.siteDocsComplete'}>
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MDropDown<boolean> id={fieldProps.field.name}
                                                                            disabled={this.isJobDisabled || this.isPlanner()}
                                                                            fieldProps={fieldProps}
                                                                            items={yesNoList}
                                                                            initialValue={false}
                                                        />
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label={'Copied to Depotnet'}
                                                               fieldName={'request.sentToDepotnet'}>
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MDropDown<boolean> id={fieldProps.field.name}
                                                                            disabled={this.isJobDisabled || this.isPlanner()}
                                                                            fieldProps={fieldProps}
                                                                            items={yesNoList}
                                                                            initialValue={false}
                                                        />
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                    </MFormSection>}

                                    <Divider/>

                                    <MFormSection title="Crew Assignments">
                                        <div className="col-12 md:col-12">
                                            <JobCrewsControl ref={this.m_jobCrewRef}
                                                             parentId={this.props.match.params.id}
                                                             msalContext={this.props.msalContext}
                                                             selectCrew={this.onSelectCrew}
                                                             selectDay={this.onSelectCrewDay}
                                                             jobStatus={this.state.form.data.request.jobStatus}
                                            />
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Drawings">
                                        <div className="col-12 md:col-12">
                                            <DrawingControl parentId={this.props.match.params.id}
                                                            msalContext={this.props.msalContext}
                                                            baseDrawingRef={this.state.form.data.request.drawingRef}
                                            />
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Files">
                                        <div className="col-12 md:col-12">
                                            <FileListControl parentId={this.props.match.params.id}
                                                             msalContext={this.props.msalContext}
                                                             selectionOnly={false}
                                            />
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Purchase Orders">
                                        <div className="col-12 md:col-12">
                                            <InvoiceControl parentId={this.props.match.params.id}
                                                            msalContext={this.props.msalContext}
                                            />
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Location">

                                        <div className="col-12 md:col-6">
                                            <MFormFieldControl label="Easting"
                                                               fieldName="request.easting">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MNumericBox id={fieldProps.field.name}
                                                                     maxLength={8}
                                                                     fieldProps={fieldProps}
                                                                     disabled={this.isFormDisabled || this.isPlanner()}
                                                                     className={fieldClasses}
                                                                     decimalPlaces={0}
                                                                     useGrouping={false}/>
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-6">
                                            <MFormFieldControl label="Northing"
                                                               fieldName="request.northing">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <MNumericBox id={fieldProps.field.name}
                                                                     maxLength={8}
                                                                     fieldProps={fieldProps}
                                                                     disabled={this.isFormDisabled || this.isPlanner()}
                                                                     className={fieldClasses}
                                                                     decimalPlaces={0}
                                                                     useGrouping={false}/>
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-12">
                                            <MFormFieldControl label="Grid Reference URL"
                                                               fieldName="request.gridReference">
                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                                    return (
                                                        <React.Fragment>
                                                            <MTextBox id={fieldProps.field.name}
                                                                      maxLength={300}
                                                                      fieldProps={fieldProps}
                                                                      disabled={this.isFormDisabled || this.isPlanner()}
                                                                      className={fieldClasses}
                                                                      onChange={e => this.m_locationValue = e.toString()}
                                                            />
                                                            <MButton
                                                                tooltip={'Browse address'}
                                                                icon={'pi pi-compass'}
                                                                disabled={this.isNavigateDisabled}
                                                                onClick={this.onNavigate_Click}
                                                            />
                                                        </React.Fragment>
                                                    );
                                                }}
                                            </MFormFieldControl>
                                        </div>
                                    </MFormSection>

                                    <MFormSection title="Address">
                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Address 1"
                                                               fieldName="request.address1">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.Address1MaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled || this.isPlanner()}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Address 2"
                                                               fieldName="request.address2">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.Address2MaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled || this.isPlanner()}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Address 3"
                                                               fieldName="request.address3">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.Address3MaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled || this.isPlanner()}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="City"
                                                               fieldName="request.city">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.CityMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled || this.isPlanner()}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="County"
                                                               fieldName="request.county">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.CountyMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled || this.isPlanner()}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>

                                        <div className="col-12 md:col-4">
                                            <MFormFieldControl label="Postcode"
                                                               fieldName="request.postcode">

                                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                                    return (
                                                        <MTextBox id={fieldProps.field.name}
                                                                  maxLength={Request.PostcodeMaxLength}
                                                                  readonly={false}
                                                                  fieldProps={fieldProps}
                                                                  disabled={this.isFormDisabled || this.isPlanner()}
                                                                  className={fieldClasses}/>
                                                    );

                                                }}

                                            </MFormFieldControl>
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                </div>
                            </div>

                            <div style={{position: "fixed", left: "65%", width: "35%", top: "200px", padding: "0 0rem"}}>
                                <div style={{overflowY: "scroll", overflowX: "hidden", maxHeight: 'calc(100vh - 260px)'}}>
                                    <MFormSection title="Map">
                                        <div className="col-12 md:col-12" style={{width: "100%", height: "35vh"}}>
                                            <MGoogleMap lat={this.state.map.lat}
                                                        long={this.state.map.lng}
                                                        zoom={this.state.map.zoom}
                                                        showMarker={this.state.map.showMarker}
                                            />
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Notes">
                                        <div className="col-12 md:col-12">
                                            <NoteListControl parentId={this.props.match.params.id}
                                                             msalContext={this.props.msalContext}
                                                             ref={this.m_noteListRef}
                                            />
                                        </div>
                                    </MFormSection>

                                    <Divider/>

                                    <MFormSection title="Audit">
                                        <div className="col-12 md:col-12">
                                            <AuditLogControl parentId={this.props.match.params.id}
                                                             msalContext={this.props.msalContext} />
                                        </div>
                                    </MFormSection>
                                </div>
                            </div>

                            <MFormFieldControl label=""
                                               fieldName="request.crewSelect">
                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <CrewSelectionDialog onSelectionChange={this.crewSelectionChange}
                                                             msalContext={this.props.msalContext}
                                                             crewTypes={[1]}
                                                             ref={this.m_crewSelectRef}
                                                             onDialogClose={(cancelled:boolean) => this.onSendToSubCrewSelectClose(fieldProps, cancelled)}
                                        />
                                    );
                                }}
                            </MFormFieldControl>

                            <MFormFieldControl label=""
                                               fieldName="request.sendSubFileSelect">
                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <FileSelectionDialog onSelectionChange={this.fileSelectionChange}
                                                             msalContext={this.props.msalContext}
                                                             ref={this.m_sendSubFileSelectRef}
                                                             onDialogClose={(cancelled: boolean) => this.onSendToSubComplete(fieldProps, cancelled)}
                                        />
                                    );
                                }}
                            </MFormFieldControl>

                            <MFormFieldControl label=""
                                               fieldName="request.sendWordayFileSelect">
                                {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                    return (
                                        <FileSelectionDialog onSelectionChange={this.fileSelectionChange}
                                                             msalContext={this.props.msalContext}
                                                             ref={this.m_workdayFileSelectRef}
                                                             onDialogClose={this.onWorkdayFileSelectionComplete}
                                                             useEmailData={true}
                                        />
                                    );
                                }}
                            </MFormFieldControl>

                        </MForm>
                    </div>
                </div>

            </MPage>

        );

    }

    private get isFormDisabled(): boolean{
        return this.isSaving;
    }

    private get isExecutingAction(): boolean {
        return this.state.pageActions !== PageActions.None;
    }

    /** */
    private get isSaving() {
        return (this.state.pageActions & PageActions.Saving) === PageActions.Saving;
    }

    private get isRefreshing() {
        return (this.state.pageActions & PageActions.Refreshing) === PageActions.Refreshing;
    }
}

export default withMsal(withRouter(EditBookingPage));
